/**
 * Audio renderer with visual card and animated bars
 */

import { Stack, Text, Box } from '@mantine/core';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui';
import { getFileIcon, getFileMimeColor } from '../../../utils';

interface AudioRendererProps {
  src: string;
  fileName: string;
  mimeType: string;
}

const BAR_COUNT = 7;
const barVariants = Array.from({ length: BAR_COUNT }, (_, i) => ({
  animate: {
    height: [8, 24 + (i % 3) * 10, 12, 28 - (i % 2) * 8, 8],
    transition: {
      duration: 1.2 + (i % 3) * 0.2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
}));

export function AudioRenderer({ src, fileName, mimeType }: AudioRendererProps) {
  const FileIcon = getFileIcon(mimeType);
  const iconColor = getFileMimeColor(mimeType);

  return (
    <Stack align="center" justify="center" h="100%" gap="xl">
      <Card variant="elevated" p="xl" style={{ minWidth: 360, maxWidth: 480 }}>
        <Stack align="center" gap="lg">
          <FileIcon size={48} color={iconColor} />
          <Text size="lg" fw={500} c="white" ta="center" lineClamp={2}>
            {fileName}
          </Text>

          {/* Animated waveform bars */}
          <Box
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              gap: 4,
              height: 40,
            }}
          >
            {barVariants.map((variant, i) => (
              <motion.div
                key={i}
                animate={variant.animate}
                style={{
                  width: 4,
                  borderRadius: 2,
                  backgroundColor: iconColor,
                  opacity: 0.7,
                }}
              />
            ))}
          </Box>

          <audio controls style={{ width: '100%' }}>
            <source src={src} type={mimeType} />
            Your browser does not support audio playback.
          </audio>
        </Stack>
      </Card>
    </Stack>
  );
}
